"use client";

import { useGSAP } from "@gsap/react";
import { flavorlists } from "@/constants";
import gsap from "gsap";
import { ScrollSmoother } from "gsap/all";
import { useRef } from "react";
import LayeredFlavorCard from "@/components/LayeredFlavorCard";

const FlavorSlider = () => {
  const sliderRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (!sliderRef.current) return;

      const mm = gsap.matchMedia();

      mm.add(
        {
          isDesktop: "(min-width: 1024px)",
          isMobile: "(max-width: 1023px)",
        },
        (context) => {
          const { isDesktop, isMobile } = context.conditions as {
            isDesktop: boolean;
            isMobile: boolean;
          };

          const section = sliderRef.current?.closest<HTMLElement>(".flavor-section");
          if (!section) return;

          if (isDesktop) {
            const track = section.querySelector<HTMLElement>(".flavor-track");
            if (!track) return;
            const hasSmoother =
              Boolean(section.closest("[data-smooth-content='true']")) ||
              Boolean(ScrollSmoother.get());

            const getDistanceUntilLastCardCentered = () => {
              const maxDistance = Math.max(0, track.scrollWidth - section.clientWidth);
              const lastCard = section.querySelector<HTMLElement>(
                `.flavor-card-${flavorlists.length - 1}`
              );

              if (!lastCard) return maxDistance;

              let offsetFromTrack = 0;
              let node: HTMLElement | null = lastCard;

              while (node && node !== track) {
                offsetFromTrack += node.offsetLeft;
                node = node.offsetParent as HTMLElement | null;
              }

              const lastCardCenter = offsetFromTrack + lastCard.offsetWidth / 2;
              const viewportCenter = section.clientWidth / 2;
              const desiredDistance = lastCardCenter - viewportCenter;

              return gsap.utils.clamp(0, maxDistance, desiredDistance);
            };

            gsap.to(track, {
              x: () => -getDistanceUntilLastCardCentered(),
              ease: "none",
              scrollTrigger: {
                trigger: section,
                start: "top top",
                end: () => `+=${getDistanceUntilLastCardCentered()}`,
                scrub: true,
                pin: section,
                pinType: hasSmoother ? "transform" : "fixed",
                pinReparent: !hasSmoother,
                anticipatePin: hasSmoother ? 1 : 0,
                invalidateOnRefresh: true,
              },
            });

            const titleNodes = [
              section.querySelector<HTMLElement>(".first-text-split"),
              section.querySelector<HTMLElement>(".second-text-split"),
              section.querySelector<HTMLElement>(".third-text-split"),
            ].filter((node): node is HTMLElement => Boolean(node));

            if (titleNodes.length > 0) {
              const xShiftByIndex = titleNodes.length === 1 ? [-8] : [-14, -10, -6];
              const titleTl = gsap.timeline({
                scrollTrigger: {
                  trigger: section,
                  start: "top top",
                  end: () => `+=${Math.max(1, getDistanceUntilLastCardCentered())}`,
                  scrub: 1.2,
                  invalidateOnRefresh: true,
                },
              });

              // Keep title readable at the start, then drift slowly.
              titleTl.to({}, { duration: 0.22 });

              titleNodes.forEach((node, index) => {
                titleTl.to(
                  node,
                  {
                    xPercent: xShiftByIndex[index] ?? -6,
                    duration: 0.78,
                    ease: "none",
                  },
                  index === 0 ? undefined : "<"
                );
              });
            }
          }

          if (isMobile) {
            flavorlists.forEach((_, index) => {
              const card = section.querySelector<HTMLElement>(`.flavor-card-${index}`);
              if (!card) return;

              gsap.fromTo(
                card,
                {
                  y: 50,
                  scale: 0.9,
                  opacity: 0,
                },
                {
                  y: 0,
                  scale: 1,
                  opacity: 1,
                  duration: 1,
                  ease: "power2.out",
                  scrollTrigger: {
                    trigger: card,
                    start: "top 85%",
                    end: "top 60%",
                    scrub: 1,
                  },
                }
              );
            });
          }
        }
      );

      return () => {
        mm.revert();
      };
    },
    { scope: sliderRef }
  );

  return (
    <div ref={sliderRef} className="slider-wrapper">
      <div className="flavors">
        {flavorlists.map((flavor, index) => {
          return (
            <a
              href={`/product/${flavor.slug}`}
              key={flavor.name}
              className={`flavor-card-${index} relative z-30 lg:w-[61vw] bg-transparent w-full lg:h-[66vh] rounded-2xl md:w-[96vw] md:h-[61vh] h-[27rem] flex-none ${flavor.rotation} block cursor-pointer`}
            >
              <LayeredFlavorCard
                title={flavor.name}
                backSrc={flavor.backImage}
                frontSrc={flavor.frontImage}
                className="rounded-xl md:rounded-2xl"
              />
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default FlavorSlider;
